import { useState } from "react";

import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";

import { DocumentResponse } from "@/api";
import { useTranslation } from "@/translations";

import { MergeFileRow } from "./MergeFileRow";
import { MergeFilesDialogProps, Rotation } from "./MergeFilesDialog.types";

interface OrderedItem {
  file: DocumentResponse;
  rotationDegrees: Rotation;
}

const ROTATION_STEP: Record<Rotation, Rotation> = { 0: 90, 90: 180, 180: 270, 270: 0 };

export function MergeFilesDialog({ files, onCancel, onConfirm, isSubmitting, fetchImageBlob }: MergeFilesDialogProps) {
  const { t } = useTranslation();

  const [items, setItems] = useState<OrderedItem[]>(() => files.map((file) => ({ file, rotationDegrees: 0 })));

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }
    setItems((prev) => {
      const oldIndex = prev.findIndex((it) => it.file.documentId === active.id);
      const newIndex = prev.findIndex((it) => it.file.documentId === over.id);
      if (oldIndex < 0 || newIndex < 0) {
        return prev;
      }
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const handleRotate = (fileId: string) => {
    setItems((prev) =>
      prev.map((it) =>
        it.file.documentId === fileId ? { ...it, rotationDegrees: ROTATION_STEP[it.rotationDegrees] } : it
      )
    );
  };

  const handleConfirm = () => {
    onConfirm(items.map((it) => ({ fileId: it.file.documentId, rotationDegrees: it.rotationDegrees })));
  };

  return (
    <Dialog open={true} onClose={isSubmitting ? undefined : onCancel} fullWidth maxWidth="md">
      <DialogTitle>{t("dropzone.merge.dialogTitle")}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("dropzone.merge.infoText")}
        </Typography>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={items.map((it) => it.file.documentId)} strategy={verticalListSortingStrategy}>
            <Stack gap={1}>
              {items.map((it) => (
                <MergeFileRow
                  key={it.file.documentId}
                  file={it.file}
                  fetchImageBlob={fetchImageBlob}
                  disabled={isSubmitting}
                  rotationDegrees={it.rotationDegrees}
                  onRotate={() => handleRotate(it.file.documentId)}
                />
              ))}
            </Stack>
          </SortableContext>
        </DndContext>
      </DialogContent>
      <DialogActions sx={{ p: 3, justifyContent: "space-between" }}>
        <Box>
          <Button onClick={onCancel} disabled={isSubmitting} variant="outlined">
            {t("dropzone.merge.cancel")}
          </Button>
        </Box>
        <Box>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={!!isSubmitting || items.length < 2}
            startIcon={
              isSubmitting ? (
                <CircularProgress size={16} color="inherit" aria-label={t("dropzone.merge.submitting")} />
              ) : undefined
            }
          >
            {t("dropzone.merge.confirm")}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
